import type { IUserInfo } from '../types'

import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { api } from '@/utils/api'
import {
  getCookieUserInfo,
  useCookieUserInfo,
} from '@/contexts/AuthContext/hooks/userCookie'
import { useUserGroup } from '@/hooks/userGroup'
import { useReqInfo } from '@/hooks/cookieConsent/useUserInEuropean'


import { useMemo, useEffect } from 'react';
export function useUserInfo() {
  const {
    isSignIn,
    isAnonymousUser,
    userId: sessionUserId,
    sessionLoading,
  } = useSessionInfo()

  const {
    data,
    isInitialLoading,
    refetch,
    status,
    error,
    isRefetching,
    isLoading,
    remove,
  } = api.user.find.useQuery(undefined, {
    enabled: isSignIn,
    cacheTime: 200,
  })

  const { removeCookieUserInfo, updateCookieUserInfo } = useCookieUserInfo()

  const userInfoCookie = useMemo(() => {
    const userInfo = getCookieUserInfo()
    if (!isSignIn && !sessionLoading) {
      /* 清空cookie用户信息缓存 */
      removeCookieUserInfo()
      return {} as IUserInfo
    }
    return userInfo
    /* 根据是否登录，重新请求等刷新cookie 数据 */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignIn, isRefetching, sessionLoading])

  useEffect(() => {
    if (isSignIn && !isRefetching) {
      updateCookieUserInfo({
        id: sessionUserId || '',
        name: data?.name || '',
        email: data?.email || '',
        firstName: data?.firstName || '',
        lastName: data?.lastName || '',
        image: data?.image || '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sessionUserId, isSignIn, sessionLoading, isRefetching])

  const userName = useMemo(() => {
    if (!isSignIn) {
      return ''
    }
    if (userInfoCookie?.name || userInfoCookie?.firstName) {
      return (
        userInfoCookie?.name ||
        `${userInfoCookie?.firstName || ''} ${
          userInfoCookie?.lastName || ''
        }`.trim()
      )
    }
    if ((data?.name || data?.firstName) && !isRefetching) {
      return (
        data?.name || `${data?.firstName || ''} ${data?.lastName || ''}`.trim()
      )
    }
    return ''
  }, [
    isSignIn,
    userInfoCookie?.name,
    userInfoCookie?.firstName,
    userInfoCookie?.lastName,
    data?.name,
    data?.firstName,
    data?.lastName,
    isRefetching,
  ])

  const userEmail = useMemo(() => {
    if (!isSignIn) {
      return ''
    }
    if (userInfoCookie?.email) {
      return userInfoCookie?.email
    }
    if (data?.email && !isRefetching) {
      return data?.email
    }
    return ''
  }, [userInfoCookie, data, isSignIn, isRefetching])

  const userImage = useMemo(() => {
    if (!isSignIn) {
      return ''
    }
    if (userInfoCookie?.image) {
      return userInfoCookie?.image
    }
    if (data?.image && !isRefetching) {
      return data?.image
    }
    return ''
  }, [isSignIn, userInfoCookie?.image, data?.image, isRefetching])

  const userId = useMemo(() => {
    if (!isSignIn) {
      return ''
    }
    if (userInfoCookie?.id) {
      return userInfoCookie?.id
    }
    if (sessionUserId) {
      return sessionUserId
    }
    return ''
  }, [userInfoCookie, isSignIn, sessionUserId])

  const initUserGroup = useMemo(() => {
    return data?.userGroup || ''
  }, [data])

  const { userGroup, userTag } = useUserGroup({ initUserGroup })
  const reqInfo = useReqInfo()

  return {
    data,
    refetch,
    loadingRefetch: isRefetching,
    isInitialLoading,
    isLoading,
    status,
    error,
    isSignIn,
    isAnonymousUser,
    userName,
    userEmail,
    userImage,
    userId,
    userGroup,
    userTag,
    reqInfo,
    remove,
  }
}

export type TUserInfo = ReturnType<typeof useUserInfo>
